import { z } from "zod";

export const transactionTypes = ["purchase", "refinance", "other"] as const;
export const TURNSTILE_ACTION = "application_submit" as const;

const cleanText = (max: number) =>
	z.string().trim().min(1, "This field is required.").max(max).refine((value) => !/[\r\n]/.test(value), "Use a single line.");

const baseApplicationSchema = z.object({
	clientSubmissionId: z.string().uuid("Refresh the form and try again."),
	transactionType: z.enum(transactionTypes),
	propertyAddresses: z.array(cleanText(240)).min(1).max(10),
	requestedLoanAmount: z.number().finite().positive("Enter the requested loan amount.").max(100_000_000, "Please call us about a request of this size."),
	propertyValue: z.number().finite().positive("Enter the purchase price or estimated value.").max(500_000_000, "Please call us about a property of this size."),
	desiredClosingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a desired closing date.").refine(
		(value) => value >= californiaDate(),
		"Choose today or a future date.",
	),
	applicantName: cleanText(120),
	phone: cleanText(30).refine((value) => value.replace(/\D/g, "").length >= 10, "Enter a valid phone number."),
	mailingAddress: cleanText(300),
	email: z.string().trim().email("Enter a valid email address.").max(254),
	useOfProceeds: z.string().trim().min(1, "Select the primary business or investment purpose.").max(120),
	useOfProceedsExplanation: z.string().trim().max(500).optional().default(""),
	dealSummary: z.string().trim().min(10, "Please add a little more detail.").max(3000),
	leadSource: z.string().trim().max(120).optional().default("Website"),
	website: z.string().max(0).optional().default(""),
	startedAt: z.number().int().positive(),
	challengeToken: z.string().min(1, "Refresh the form and try again.").max(4096),
	turnstileToken: z.string().min(1, "Complete the security check.").max(4096),
});

export const applicationSchema = baseApplicationSchema.superRefine((application, context) => {
	if (application.useOfProceeds === "Other business or investment purpose" && !application.useOfProceedsExplanation) {
		context.addIssue({ code: "custom", path: ["useOfProceedsExplanation"], message: "Briefly explain the other business or investment purpose." });
	}
});

export function californiaDate(date = new Date()): string {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/Los_Angeles",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(date);
	const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
	return `${value.year}-${value.month}-${value.day}`;
}

export function previewApplicationId(randomId = crypto.randomUUID()): string {
	const token = randomId.replace(/[^a-z0-9]/gi, "").slice(0, 8).toUpperCase().padEnd(8, "0");
	return `PREVIEW-${token}`;
}

export type ApplicationInput = z.infer<typeof applicationSchema>;

export function createEmptyApplication(): ApplicationInput {
	return {
	clientSubmissionId: crypto.randomUUID(),
	transactionType: "purchase",
	propertyAddresses: [""],
	requestedLoanAmount: 0,
	propertyValue: 0,
	desiredClosingDate: "",
	applicantName: "",
	phone: "",
	mailingAddress: "",
	email: "",
	useOfProceeds: "",
	useOfProceedsExplanation: "",
	dealSummary: "",
	leadSource: "Website",
	website: "",
	startedAt: Date.now(),
	challengeToken: "",
	turnstileToken: "",
	};
}

export function leadSourceFromQuery(source: string | null): string {
	return ({ email_signature: "Email Signature", organic: "Organic", direct: "Direct", broker_referral: "Broker Referral" } as Record<string, string>)[source || ""] || "Website";
}

export function transactionLabel(type: ApplicationInput["transactionType"]): string {
	return {
		purchase: "Purchase",
		refinance: "Refinance / Business-Purpose Cash-Out",
		other: "Other",
	}[type];
}
